import { $httpFhirApi } from '@/common/api/httpFhir.service';

// Who may authorize a release of information. Kept apart from useConsentFlow, whose useTaskFlow
// import closes a cycle through useEncounterFlow, so the form fillers can load these in any order.

// "A", "A and B", "A, B and C". The conjunction is the caller's because the two rules differ:
// staying on the Signers list takes any one authorizer, activating takes every one of them.
export const joinAuthorizerNames = (names, conjunction) => {
    const listed = (names || []).filter(Boolean);
    if (listed.length <= 1) return listed[0] || '';
    return listed.slice(0, -1).join(', ') + ' ' + conjunction + ' ' + listed[listed.length - 1];
};

// Relationship codes (v2-0131) that transfer authority. Authority is recorded, never calculated —
// it comes from an active designation, not from the patient's age.
const ROI_AUTHORIZER_CODES = ['G', 'P'];

// Every active guardian or POA designation, or the patient when none names anyone else. Two
// guardians is plural authority, not ambiguity: both are seeded and the preparer trims the list.
export const deriveRoiAuthorizers = (patient, relationships) => {
    if (!patient?.id) return { authorizers: [], source: 'unknownPatient' };

    const designations = (relationships || []).filter(
        (relationship) =>
            relationship?.resourceType === 'BN_Relationship' &&
            relationship.active === true &&
            relationship.subject?.id === patient.id &&
            relationship.related?.id &&
            (relationship.relationship || []).some(({ code }) => ROI_AUTHORIZER_CODES.includes(code)),
    );

    // One contact is one authorizer, whether both flags sit on one row or on two rows naming them.
    // Keyed by id so order stays the order the designations were read in.
    const designees = [...new Map(designations.map((relationship) => [relationship.related.id, relationship.related])).values()];

    if (!designees.length) {
        return { authorizers: [{ id: patient.id, resourceType: patient.resourceType || 'Patient' }], source: 'patient' };
    }
    return { authorizers: designees.map((related) => ({ id: related.id, resourceType: related.resourceType || 'Patient' })), source: 'designation' };
};

// The filler seeds signers before the QuestionnaireResponse exists, so its relationship DataProvider
// is still empty. Returns null, never [], when unreadable — [] would read as "no guardian on file".
export const fetchRelationshipsForPatients = async (patientIds) => {
    const ids = [...new Set((patientIds || []).filter((id) => id))];
    if (!ids.length) return null;
    try {
        const response = await $httpFhirApi.get('BN_Relationship?subject=' + ids.join(','));
        return response.data || [];
    } catch (error) {
        console.error('Failed to load relationships for ROI authorizer derivation', ids, error);
        return null;
    }
};
